import { fileUploadFromUrl } from '@api/form-verification';
import { useCallback, useEffect, useRef } from 'react';

export type StructuredAddress = {
  address: string | null,
  locality: string | null, // neighbourhood/locality
  district: string | null, // city/district
  state: string | null,
  country: string | null,
  pincode: string | null
};

export type GeoAndIP = {
  latitude: number | null,
  longitude: number | null,
  ipAddress: string | null,
  accuracy: number | null,
  timestamp: number | null,
  address: StructuredAddress | null,
  mapImage: string | null,
  error?: string
};

// Replace with your Google Maps API key
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Helper: extract structured address from Google Geocoding API response
export function extractStructuredAddress(geocodingData: any): StructuredAddress {
  if (!geocodingData?.results?.[0]) {
    return {
      address: null,
      locality: null,
      district: null,
      state: null,
      country: null,
      pincode: null
    };
  }

  const result = geocodingData.results[0];
  const components = result.address_components || [];

  const findComponent = (types: string[]) => {
    return components.find((component: any) =>
      types.some((type) => component.types.includes(type))
    );
  };

  // Extract locality/neighbourhood (sublocality_level_2, neighborhood, or locality)
  const localityComponent =
    findComponent(['sublocality_level_2', 'neighborhood', 'sublocality']) ||
    findComponent(['locality']);

  // Extract district/city (locality, administrative_area_level_2, or administrative_area_level_3)
  const districtComponent =
    findComponent(['locality']) ||
    findComponent(['administrative_area_level_3']) ||
    findComponent(['administrative_area_level_2']);

  // Extract state (administrative_area_level_1)
  const stateComponent = findComponent(['administrative_area_level_1']);

  // Extract country
  const countryComponent = findComponent(['country']);

  // Extract postal code
  const pincodeComponent = findComponent(['postal_code']);

  return {
    address: result.formatted_address || null,
    locality: localityComponent?.long_name || null,
    district: districtComponent?.long_name || null,
    state: stateComponent?.long_name || null,
    country: countryComponent?.long_name || null,
    pincode: pincodeComponent?.long_name || null
  };
}

// Helper: fetch structured address from Google Geocoding API
async function fetchStructuredAddress(lat: number, lng: number): Promise<StructuredAddress> {
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
    );
    const data = await res.json();
    if (data.status === 'OK') {
      return extractStructuredAddress(data);
    }
    return {
      address: null,
      locality: null,
      district: null,
      state: null,
      country: null,
      pincode: null
    };
  } catch (err) {
    console.error('Failed to fetch structured address:', err);
    return {
      address: null,
      locality: null,
      district: null,
      state: null,
      country: null,
      pincode: null
    };
  }
}

// Helper: fetch IP address
async function fetchIP(): Promise<string> {
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    return data.ip || '';
  } catch (err) {
    console.error('Failed to fetch IP:', err);
    return '';
  }
}

// Helper: get Google Static Map image URL
async function getStaticMapUrl(
  lat: number,
  lng: number,
  zoom = 15,
  width = 600,
  height = 400,
  verificationName: string
): Promise<string> {
  const res = await fileUploadFromUrl(
    `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=${width}x${height}&markers=color:red%7C${lat},${lng}&key=${GOOGLE_API_KEY}`,
    `${verificationName}-geo-tag-image`
  );
  return res?.data?.fileUrl || '';
}

export function useGeoAndIP() {
  const geoRef = useRef<GeoAndIP>({
    latitude: null,
    longitude: null,
    accuracy: null,
    timestamp: null,
    address: null,
    mapImage: null,
    ipAddress: null,
    error: undefined
  });

  const watchIdRef = useRef<number | null>(null);

  // Start tracking live position
  const startTracking = useCallback(async () => {
    if (!navigator.geolocation) {
      geoRef.current = {
        ...geoRef.current,
        error: 'Geolocation is not supported by this browser.'
      };
      return;
    }

    // Fetch IP once
    const ip = await fetchIP();

    geoRef.current = { ...geoRef.current, ipAddress: ip, error: undefined };

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const timestamp = pos.timestamp;
        geoRef.current = {
          ...geoRef.current,
          latitude,
          longitude,
          accuracy,
          timestamp,
          error: undefined
        };
      },
      (err) => {
        console.log('err', err);
        geoRef.current = { ...geoRef.current, error: err.message };
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
  }, []);

  // Stop live tracking
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  // Return current coords snapshot
  const getCoords = useCallback(async (verificationName: string = '') => {
    const currentGeo = geoRef.current;
    if (currentGeo.latitude && currentGeo.longitude) {
      const [address, mapImage] = await Promise.all([
        fetchStructuredAddress(currentGeo.latitude, currentGeo.longitude),
        getStaticMapUrl(currentGeo.latitude, currentGeo.longitude, 15, 600, 400, verificationName)
      ]);

      return {
        ...currentGeo,
        address,
        mapImage
      };
    }
    return currentGeo;
  }, []);

  useEffect(() => {
    return () => stopTracking();
  }, []);

  return { getCoords, startTracking, stopTracking, error: geoRef.current.error };
}
